/**
 * maxCeiling — the pure core of the `maxFieldWithFallback` validation.
 *
 * The rule caps a numeric field against the FIRST usable value in an ordered,
 * admin-authored list of other fields ("cap at the To value, else the From
 * value"). Both AddFormV1 and EditFormV1 hold a thin antd validator around
 * these functions; everything decision-shaped lives here so it can be unit
 * tested without rendering a form (importing either form component pulls in
 * react-pdf, which dies on jsdom's missing DOMMatrix).
 *
 * Nothing here knows a module, group or field name — the list of compare
 * fields comes from the validation entry in Form Groups config.
 */

/**
 * Resolve a compare-field reference to an absolute form name path, relative to
 * the field being validated. Kept identical to the behaviour both forms had
 * inline (they now delegate here):
 *
 *   • DOTTED reference ("jobClientRate.clientRateTo") → ABSOLUTE path, i.e. it
 *     reaches out of the validated field's own container. This is what lets a
 *     field inside one container (candidateBudget.*) be capped by a value
 *     parked in a different one — inside a Form.List row the row prefix is
 *     preserved instead, so a per-row rule still resolves within its row.
 *   • BARE reference ("clientRateTo") → sibling of the validated field, i.e.
 *     the last path segment is swapped. A bare name can therefore never see a
 *     top-level field from inside a container — use a dotted reference for that.
 */
export function resolveCompareFieldName(currentName, compareField) {
    const compareParts = String(compareField).split('.').filter(Boolean);
    if (!Array.isArray(currentName)) return compareParts.length > 1 ? compareParts : compareField;

    if (compareParts.length > 1) {
        // A DB rule may use `endDate` or `work_experience.endDate`. Preserve
        // the current Form.List row and avoid repeating the group segment.
        if (currentName.length >= 3 && typeof currentName[1] === 'number') {
            const listPrefix = currentName.slice(0, 2);
            const relativeParts = compareParts[0] === String(currentName[0])
                ? compareParts.slice(1)
                : compareParts;
            return [...listPrefix, ...relativeParts];
        }
        return compareParts;
    }

    const nextName = [...currentName];
    nextName[nextName.length - 1] = compareParts[0];
    return nextName;
}

/**
 * readCompareValue — read ONE compare field's current value, tolerantly.
 *
 * The ceiling for these rules is routinely parked in a HIDDEN carrier field
 * (show:0, payloadMode:"skip") that some other mechanism fills in — on a
 * submission, the job's client rate arrives that way via prefillFromModule.
 * A carrier is never rendered, so its value only ever reaches the store
 * through setFieldsValue, and it can land in one of two shapes:
 *
 *   • the NESTED name path  {jobClientRate: {clientRateTo: 68}}   — correct
 *   • the LITERAL dotted key {"jobClientRate.clientRateTo": 68}   — what a
 *     plain `values[field.field] = …` assignment produces, since rc-field-form
 *     treats a dotted string as ONE field name rather than a path
 *
 * Only the first is visible to a name-path lookup. Reading just that one means
 * a carrier written the other way is invisible, `selectCeiling` finds nothing,
 * and the rule FAILS OPEN — the cap silently disappears and an over-limit value
 * is accepted with no error at all. Which is strictly worse than having no cap:
 * the rule looks present in config and enforces nothing.
 *
 * So both shapes are read, nested first. Costs one extra store lookup and makes
 * the cap independent of how its ceiling happened to be written.
 */
export function readCompareValue(form, currentName, compareField) {
    const nested = form?.getFieldValue?.(resolveCompareFieldName(currentName, compareField));
    if (nested !== undefined && nested !== null && nested !== '') return nested;
    return form?.getFieldValue?.(compareField);
}

/** "a, b ,, c" → ['a','b','c'] — the admin writes the list comma-separated. */
export function parseCompareFields(value) {
    return String(value ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

/**
 * Pick the ceiling: the first compare field that carries a USABLE maximum.
 *
 * Usable means "a positive, finite number". Empty/blank falls through (the
 * long-standing behaviour) and so does ZERO or a negative number, which is the
 * subtle part:
 *
 *   A 0 ceiling means "no ceiling configured", NOT "nothing is allowed".
 *   Rate ranges in this system are routinely persisted with the open end at 0
 *   (submissions store candidateBudgetEnd: 0 on nearly every record; jobs with
 *   a single-point budget leave clientBudgetEnd unset or 0). Treating that 0 as
 *   a real maximum would reject EVERY value the user could type, with an error
 *   they have no way to satisfy. The rest of the codebase already reads these
 *   pairs the same way — see popups/definitions/others-assign/config.js
 *   (`end > 0 ? end : start`) and financeApprovalEngine's `clientBudgetEnd ||
 *   clientBudgetStart`. So a non-positive candidate is skipped and the next
 *   entry in the list is tried; if none qualifies there is simply no cap.
 *
 * @param {string[]} compareFields ordered field references
 * @param {(field: string) => any} readValue reads the current value of one
 * @returns {{ field: string, maximum: number } | null}
 */
export function selectCeiling(compareFields, readValue) {
    for (const field of compareFields ?? []) {
        const raw = readValue(field);
        if (raw === undefined || raw === null || raw === '') continue;
        const maximum = Number(raw);
        if (!Number.isFinite(maximum) || maximum <= 0) continue;
        return { field, maximum };
    }
    return null;
}

/**
 * The whole decision: is `input` within the first usable ceiling?
 * Fails open (ok:true) for a blank input, a non-numeric input, and when no
 * compare field carries a usable ceiling.
 *
 * @returns {{ ok: boolean, field: string|null, maximum: number|null }}
 */
export function checkMaxWithFallback({ input, compareFields, readValue }) {
    const pass = { ok: true, field: null, maximum: null };
    if (input === undefined || input === null || input === '') return pass;

    const ceiling = selectCeiling(compareFields, readValue);
    if (!ceiling) return pass;

    const inputNumber = Number(input);
    if (!Number.isFinite(inputNumber)) return pass;

    return {
        ok: inputNumber <= ceiling.maximum,
        field: ceiling.field,
        maximum: ceiling.maximum,
    };
}
